/**
 * RENDERING ADMIN-AUTHORED CONTENT WITHOUT HANDING IT THE PAGE.
 *
 * Legal pages, FAQ answers and news posts are free text in the database. An
 * administrator writes them, which is a reason to be careful rather than a
 * reason to relax: the admin account is exactly what an attacker goes for, and
 * a `<script>` in a legal page is a STORED XSS served to every visitor,
 * signed-in or not, on the one page people read before they trust us.
 *
 * ── THE RULE THIS FILE ENFORCES ─────────────────────────────────────────────
 * Nothing here ever assigns `innerHTML` on a node that is in, or will be in,
 * the live document. Two consequences worth stating, because they are the
 * whole security argument:
 *
 *  1. Untrusted markup is parsed by `DOMParser` into an INERT document. An
 *     inert document runs no script, loads no image, fires no event handler.
 *  2. The result is not sanitised in place and then adopted — it is COPIED,
 *     node by node, into freshly created elements in the target document, with
 *     an allowlist deciding every tag and every attribute. Anything not on the
 *     list never becomes a node at all.
 *
 * That second point is what defends against mutation XSS. mXSS works by
 * finding a string that means one thing when the sanitiser parses it and
 * another when the browser re-parses the sanitiser's OUTPUT — so it needs a
 * serialise/re-parse round trip. There isn't one here. We parse once and build
 * nodes; the output is never turned back into a string.
 *
 * ── WHY NOT A LIBRARY ───────────────────────────────────────────────────────
 * DOMPurify is the right answer for arbitrary hostile HTML and would be worth
 * its ~20 KB if we needed it. This surface is small enough to state
 * completely: block-level prose, lists, links, images. An allowlist that
 * REBUILDS rather than strips is the conservative construction, and it is
 * about 120 lines that the test file can attack directly. If the content model
 * ever grows to accept arbitrary embedded HTML, take the dependency.
 *
 * ── THE TWO INPUT SHAPES ────────────────────────────────────────────────────
 * `LegalPage.content` is markdown (the seed stores `content/legal/*.md`
 * verbatim so the sha256 in a consent record matches a file in git). News and
 * FAQ bodies may be either, depending on what the admin typed. So
 * `renderRichText` sniffs: markup goes through the sanitiser, everything else
 * through the markdown reader — which builds nodes directly and therefore
 * cannot emit an element the allowlist would have refused.
 */

/* ═══════════════════════════════════════════════════════════ the allowlist ══ */

import { createSlotIcon } from '@/engine/ui/icon-registry';

/** Elements copied through as themselves. */
export const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  'p', 'br', 'hr',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'sup', 'sub', 'small', 'mark',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'a', 'img',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
  'dl', 'dt', 'dd',
  'figure', 'figcaption',
]);

/**
 * Elements dropped WITH their contents.
 *
 * The distinction from "unknown" matters. An unknown tag is unwrapped, because
 * `<div>text</div>` should keep its text. `<style>body{…}</style>` must not —
 * unwrapping it would print the CSS as prose, and worse, `<script>` unwrapped
 * would put its source into the page as text that a later innerHTML somewhere
 * else could revive.
 */
export const DROPPED_TAGS: ReadonlySet<string> = new Set([
  'script', 'style', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet',
  'template', 'noscript', 'base', 'link', 'meta', 'title', 'head',
  'form', 'input', 'button', 'select', 'option', 'textarea', 'label', 'fieldset',
  'svg', 'math', 'canvas', 'audio', 'video', 'source', 'track', 'portal', 'dialog',
]);

/** Attributes kept, per tag. Everything else — including every `on*` — is dropped. */
const ALLOWED_ATTRS: Readonly<Record<string, readonly string[]>> = {
  a: ['href', 'title'],
  img: ['src', 'alt', 'title', 'width', 'height'],
  ol: ['start'],
  td: ['colspan', 'rowspan'],
  th: ['colspan', 'rowspan', 'scope'],
};

/** URL schemes a link may use. Note the absence of `data:` and `javascript:`. */
const SAFE_LINK_SCHEMES = new Set(['http:', 'https:', 'mailto:']);

/** Schemes an image may use. `data:` is excluded: `data:image/svg+xml` is script. */
const SAFE_IMAGE_SCHEMES = new Set(['http:', 'https:']);

/**
 * Nesting a hostile document can force. Past this the subtree is dropped: a
 * legitimate legal page is not 64 elements deep, and a recursive walk over
 * 100 000 nested tags is a denial of service on the render thread.
 */
const MAX_DEPTH = 64;

/* ══════════════════════════════════════════════════════════════════ URLs ══ */

/**
 * Decide whether a URL may be used, and return the form to write back.
 *
 * THE DEFENCE IS THE SCHEME ALLOWLIST, not string matching. `javascript:`
 * written as `java\tscript:` or `JaVaScRiPt:` is refused because the URL
 * parser normalises it to the `javascript:` protocol and that protocol is not
 * in the set — a `startsWith('javascript:')` test would miss both.
 *
 * There is deliberately NO hand-rolled `javascript` string filter here. An
 * earlier draft stripped control characters and interior whitespace to defeat
 * `java\nscript:`; mutation testing showed removing that line failed nothing,
 * because the parser normalises those anyway and the allowlist catches the
 * result. Untested defensive code that reads as the defence is worse than
 * none: the next person trusts it. What remains is `trim()`, whose effect the
 * tests do pin.
 *
 * Rules, in the order they are applied:
 *  - A fragment (`#terms`) or a rooted path (`/legal/privacy`) is allowed.
 *  - `//host/path` is refused: it is protocol-relative and reads as a path.
 *  - Anything else must parse as an absolute URL with an allowed scheme.
 */
export function safeUrl(raw: string, schemes: ReadonlySet<string> = SAFE_LINK_SCHEMES): string | null {
  const cleaned = raw.trim();
  if (cleaned === '') return null;
  if (cleaned.startsWith('#')) return cleaned;
  if (cleaned.startsWith('//')) return null;
  if (cleaned.startsWith('/')) return cleaned;
  try {
    // `URL` needs a base to parse anything relative. The base is a hostname
    // that cannot exist, and a result still sitting on it means the input was
    // a BARE relative reference (`terms`, `../privacy`) — refused, because
    // what it resolves against depends on the page it happens to be rendered
    // on, and the same document is served from several routes. Rooted paths
    // and fragments were allowed above precisely because they are unambiguous.
    const url = new URL(cleaned, 'https://invalid.example');
    if (!schemes.has(url.protocol)) return null;
    if (url.protocol !== 'mailto:' && url.hostname === 'invalid.example') return null;
    return url.href;
  } catch {
    return null;
  }
}

/** True when `href` points at another origin, so the link should open away. */
export function isExternalUrl(href: string): boolean {
  if (href.startsWith('#') || href.startsWith('/')) return false;
  if (href.startsWith('mailto:')) return false;
  try {
    return new URL(href, location.href).origin !== location.origin;
  } catch {
    return false;
  }
}

/**
 * Finish an anchor: safe `rel`, external links opening in a new tab, and a
 * screen-reader-only note saying so — an unannounced new tab is a WCAG 3.2.5
 * problem, and sighted users get the same warning from the `title`.
 */
function decorateAnchor(a: HTMLAnchorElement, externalLabel: string): void {
  a.rel = 'noopener noreferrer';
  if (!isExternalUrl(a.getAttribute('href') ?? '')) return;
  a.target = '_blank';
  const note = a.ownerDocument.createElement('span');
  note.className = 'sr-only';
  note.textContent = ` (${externalLabel})`;
  // A VISIBLE mark too (decorative; the sr-only note is the spoken one): the
  // external-link icon slot, so a sighted reader is warned before the tab opens.
  const mark = createSlotIcon(a.ownerDocument, 'action-external', { size: 14, class: 'ml-1 align-[-2px]' });
  mark.style.display = 'inline-flex';
  a.appendChild(mark);
  a.appendChild(note);
}

/* ══════════════════════════════════════════════════════════════ sanitiser ══ */

export interface RichTextOptions {
  /** Announced after external links. Pass `t('a11y.external_link')`. */
  readonly externalLinkLabel?: string;
  /** Target document. Defaults to the live one; tests pass their own. */
  readonly doc?: Document;
}

const DEFAULT_EXTERNAL_LABEL = 'Opens in a new tab';

/**
 * Copy an inert node into `doc`, applying the allowlist. Returns null when the
 * node and its subtree are dropped entirely.
 */
function copyNode(node: Node, doc: Document, depth: number, label: string): Node | null {
  if (node.nodeType === 3 /* TEXT_NODE */) {
    return doc.createTextNode(node.nodeValue ?? '');
  }
  if (node.nodeType !== 1 /* ELEMENT_NODE */) {
    // Comments, processing instructions, CDATA: no reason to render any of
    // them, and `<!--[if IE]><script>` is a real historical parser trick.
    return null;
  }
  if (depth > MAX_DEPTH) return null;

  const source = node as Element;
  const tag = source.localName.toLowerCase();
  if (DROPPED_TAGS.has(tag)) return null;

  if (!ALLOWED_TAGS.has(tag)) {
    // Unknown but not dangerous (`div`, `span`, `section`, a custom element):
    // keep the children, lose the wrapper.
    const fragment = doc.createDocumentFragment();
    appendChildren(source, fragment, doc, depth + 1, label);
    return fragment;
  }

  const out = doc.createElement(tag);
  const permitted = ALLOWED_ATTRS[tag] ?? [];
  for (const name of permitted) {
    const value = source.getAttribute(name);
    if (value === null) continue;
    if (name === 'href') {
      const url = safeUrl(value, SAFE_LINK_SCHEMES);
      if (url !== null) out.setAttribute('href', url);
      continue;
    }
    if (name === 'src') {
      const url = safeUrl(value, SAFE_IMAGE_SCHEMES);
      if (url !== null) out.setAttribute('src', url);
      continue;
    }
    if (name === 'width' || name === 'height' || name === 'colspan' || name === 'rowspan' || name === 'start') {
      if (/^\d{1,5}$/.test(value.trim())) out.setAttribute(name, value.trim());
      continue;
    }
    if (name === 'scope') {
      if (value === 'row' || value === 'col') out.setAttribute('scope', value);
      continue;
    }
    out.setAttribute(name, value);
  }

  // An anchor with no surviving href is not a link. Keeping the <a> would
  // render text that looks clickable and does nothing.
  if (tag === 'a' && !out.hasAttribute('href')) {
    const fragment = doc.createDocumentFragment();
    appendChildren(source, fragment, doc, depth + 1, label);
    return fragment;
  }
  // An image whose src was refused is a broken icon and an alt string.
  if (tag === 'img' && !out.hasAttribute('src')) return null;

  if (tag === 'img') {
    out.setAttribute('loading', 'lazy');
    if (!out.hasAttribute('alt')) out.setAttribute('alt', '');
    out.className = 'max-w-full h-auto rounded';
  }

  appendChildren(source, out, doc, depth + 1, label);
  if (tag === 'a') decorateAnchor(out as HTMLAnchorElement, label);
  return out;
}

function appendChildren(source: Element, target: Node, doc: Document, depth: number, label: string): void {
  for (const child of Array.from(source.childNodes)) {
    const copy = copyNode(child, doc, depth, label);
    if (copy !== null) target.appendChild(copy);
  }
}

/**
 * Parse untrusted HTML and return a fragment containing only allowlisted
 * nodes, built in `options.doc`.
 */
export function sanitiseHtml(html: string, options: RichTextOptions = {}): DocumentFragment {
  const doc = options.doc ?? document;
  const label = options.externalLinkLabel ?? DEFAULT_EXTERNAL_LABEL;
  const fragment = doc.createDocumentFragment();
  if (html.trim() === '') return fragment;

  // INERT. Nothing in `parsed` executes, loads or fires.
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  appendChildren(parsed.body, fragment, doc, 0, label);
  return fragment;
}

/* ═══════════════════════════════════════════════════════════════ markdown ══ */

/**
 * The markdown subset the legal documents are written in.
 *
 * Supported: ATX headings, paragraphs, unordered and ordered lists,
 * blockquotes, thematic breaks, and inline `**strong**`, `*emphasis*`,
 * `` `code` `` and `[links](url)`.
 *
 * NOT supported, on purpose: raw HTML blocks (that is what the sanitiser is
 * for, and mixing the two means one parser's output feeds the other's input),
 * nested lists, reference links, tables, and fenced code blocks. If a document
 * needs one of those, add it here with a test rather than pasting HTML into
 * the database.
 *
 * ── HEADING LEVELS ARE NORMALISED, NOT DEMOTED BY A FIXED AMOUNT ────────────
 * The page renders the document's title as its `<h1>`, so a heading in the
 * body must never be one — two `<h1>`s leave a screen reader with no document
 * outline. But a flat "+1" is wrong too: `content/legal/*.md` carries its
 * title in front matter and starts its sections at `##`, so +1 would produce a
 * document made entirely of `<h3>`s with no `<h2>` above them. A heading
 * hierarchy that starts at level 3 is exactly as broken as one that starts at
 * level 1, and it silently empties any table of contents built from `<h2>`.
 *
 * So the SHALLOWEST heading in the document becomes `<h2>` and the rest keep
 * their relative depth, to a floor of `<h6>`. `#`/`##` and `##`/`###` both
 * come out as `<h2>`/`<h3>`, and an author cannot break the page's outline by
 * choosing a different top level.
 */
export function renderMarkdown(markdown: string, options: RichTextOptions = {}): DocumentFragment {
  const doc = options.doc ?? document;
  const label = options.externalLinkLabel ?? DEFAULT_EXTERNAL_LABEL;
  const out = doc.createDocumentFragment();
  const lines = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

  // One pass to find the shallowest heading, so every heading can be placed
  // relative to it. `6` when there are none — nothing then uses it.
  let topLevel = 6;
  for (const line of lines) {
    const found = /^(#{1,6})\s+\S/.exec(line);
    if (found !== null) topLevel = Math.min(topLevel, found[1].length);
  }

  let i = 0;
  const isBlank = (s: string): boolean => s.trim() === '';

  while (i < lines.length) {
    const line = lines[i];

    if (isBlank(line)) { i++; continue; }

    // Thematic break — before the list check, or `---` reads as nothing.
    if (/^\s{0,3}(\*\s*\*\s*\*|-\s*-\s*-|_\s*_\s*_)[\s*\-_]*$/.test(line)) {
      out.appendChild(doc.createElement('hr'));
      i++;
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading !== null) {
      const level = Math.min(2 + (heading[1].length - topLevel), 6);
      const h = doc.createElement(`h${level}`);
      h.appendChild(inlineToFragment(heading[2].trim(), doc, label));
      out.appendChild(h);
      i++;
      continue;
    }

    if (/^\s{0,3}>\s?/.test(line)) {
      const block: string[] = [];
      while (i < lines.length && /^\s{0,3}>\s?/.test(lines[i])) {
        block.push(lines[i].replace(/^\s{0,3}>\s?/, ''));
        i++;
      }
      const quote = doc.createElement('blockquote');
      quote.appendChild(renderMarkdown(block.join('\n'), { doc, externalLinkLabel: label }));
      out.appendChild(quote);
      continue;
    }

    const bullet = /^\s{0,3}[-*+]\s+(.*)$/;
    const numbered = /^\s{0,3}\d{1,9}[.)]\s+(.*)$/;
    if (bullet.test(line) || numbered.test(line)) {
      const ordered = !bullet.test(line);
      const pattern = ordered ? numbered : bullet;
      const list = doc.createElement(ordered ? 'ol' : 'ul');
      while (i < lines.length) {
        const item = pattern.exec(lines[i]);
        if (item === null) {
          // A plain line directly under an item continues it (soft wrap).
          if (!isBlank(lines[i]) && !bullet.test(lines[i]) && !numbered.test(lines[i]) && list.lastElementChild !== null) {
            list.lastElementChild.appendChild(doc.createTextNode(' '));
            list.lastElementChild.appendChild(inlineToFragment(lines[i].trim(), doc, label));
            i++;
            continue;
          }
          break;
        }
        const li = doc.createElement('li');
        li.appendChild(inlineToFragment(item[1].trim(), doc, label));
        list.appendChild(li);
        i++;
      }
      out.appendChild(list);
      continue;
    }

    const paragraph: string[] = [];
    while (
      i < lines.length &&
      !isBlank(lines[i]) &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !/^\s{0,3}>\s?/.test(lines[i]) &&
      !bullet.test(lines[i]) &&
      !numbered.test(lines[i])
    ) {
      paragraph.push(lines[i].trim());
      i++;
    }
    const p = doc.createElement('p');
    p.appendChild(inlineToFragment(paragraph.join(' '), doc, label));
    out.appendChild(p);
  }

  return out;
}

/**
 * `**strong**`, `*em*`, `` `code` ``, `[text](url)`. Everything else is text.
 *
 * The URL group tolerates ONE level of balanced parentheses, so
 * `[x](javascript:alert(1))` is captured whole. A lazier `[^\s)]+` would stop
 * at the first `)` and leave the tail as visible text — which for a REFUSED
 * link means printing a fragment of the payload onto the page.
 */
const INLINE =
  /\*\*([^*]+)\*\*|\*([^*\n]+)\*|`([^`\n]+)`|\[([^\]\n]+)\]\(((?:[^\s()]|\([^\s()]*\))+)\)/;

function inlineToFragment(text: string, doc: Document, label: string): DocumentFragment {
  const out = doc.createDocumentFragment();
  let rest = text;

  while (rest !== '') {
    const match = INLINE.exec(rest);
    if (match === null || match.index === undefined) break;

    if (match.index > 0) out.appendChild(doc.createTextNode(rest.slice(0, match.index)));

    if (match[1] !== undefined) {
      const strong = doc.createElement('strong');
      strong.appendChild(inlineToFragment(match[1], doc, label));
      out.appendChild(strong);
    } else if (match[2] !== undefined) {
      const em = doc.createElement('em');
      em.appendChild(inlineToFragment(match[2], doc, label));
      out.appendChild(em);
    } else if (match[3] !== undefined) {
      const code = doc.createElement('code');
      code.textContent = match[3];
      out.appendChild(code);
    } else if (match[4] !== undefined && match[5] !== undefined) {
      const href = safeUrl(match[5], SAFE_LINK_SCHEMES);
      if (href === null) {
        // A refused URL leaves the link TEXT, never a dead anchor and never
        // the URL itself — `[click](javascript:…)` must not print the payload.
        out.appendChild(doc.createTextNode(match[4]));
      } else {
        const a = doc.createElement('a');
        a.setAttribute('href', href);
        a.appendChild(inlineToFragment(match[4], doc, label));
        decorateAnchor(a, label);
        out.appendChild(a);
      }
    }

    rest = rest.slice(match.index + match[0].length);
  }

  if (rest !== '') out.appendChild(doc.createTextNode(rest));
  return out;
}

/* ═════════════════════════════════════════════════════════════ the front door ══ */

/**
 * True when the string carries markup. Requires a letter or `/` straight after
 * the `<`, so prose containing "a < b" is still prose.
 */
export function looksLikeHtml(raw: string): boolean {
  return /<\/?[a-z][a-z0-9-]*(\s[^<>]*)?\/?>/i.test(raw);
}

/**
 * Render database content into `target`, replacing whatever was there.
 *
 * This is the only function pages should call. It picks the parser, and both
 * paths end in allowlisted nodes built in the target's document.
 */
export function renderRichText(target: HTMLElement, raw: string, options: RichTextOptions = {}): void {
  target.replaceChildren();
  const opts: RichTextOptions = { ...options, doc: options.doc ?? target.ownerDocument };
  target.appendChild(looksLikeHtml(raw) ? sanitiseHtml(raw, opts) : renderMarkdown(raw, opts));
}

/* ════════════════════════════════════════════════════════════ presentation ══ */

/**
 * Utility classes applied to rendered content, by tag.
 *
 * WHY THIS IS DONE IN JS AND NOT IN CSS. The rendered nodes come from the
 * database, so they carry no classes of our own, and Tailwind has no
 * typography plugin installed here. The alternatives were a descendant-selector
 * block in `src/styles/app.css` — a file three other screens are being built
 * against at the same time, so a merge conflict for a cosmetic gain — or this,
 * which is self-contained and testable. It is deliberately plain: the visual
 * pass is a later phase, and this only has to be legible and correctly spaced.
 */
const PROSE_CLASSES: Readonly<Record<string, string>> = {
  h2: 'font-heading text-xl text-gold mt-8 mb-2 scroll-mt-4',
  h3: 'font-heading text-lg text-text mt-6 mb-2',
  h4: 'font-heading text-base text-text mt-4 mb-2',
  h5: 'font-heading text-base text-text-dim mt-4 mb-1',
  h6: 'font-heading text-sm text-text-dim mt-4 mb-1',
  p: 'mb-4 leading-relaxed',
  ul: 'mb-4 ps-6 list-disc space-y-1',
  ol: 'mb-4 ps-6 list-decimal space-y-1',
  li: 'leading-relaxed',
  blockquote: 'mb-4 ps-4 border-s-2 border-gold/60 text-text-dim italic',
  hr: 'my-8 border-t border-border',
  code: 'px-1 py-0.5 rounded bg-surface text-sm',
  pre: 'mb-4 p-3 rounded bg-surface overflow-x-auto text-sm',
  table: 'mb-4 w-full text-start border-collapse',
  th: 'text-start p-2 border border-border font-semibold',
  td: 'p-2 border border-border align-top',
  figure: 'mb-4',
  figcaption: 'text-sm text-text-dim mt-1',
  a: 'text-gold underline underline-offset-2',
};

/** Style a rendered content tree in place. Safe to call more than once. */
export function applyProseClasses(container: HTMLElement): void {
  for (const [tag, classes] of Object.entries(PROSE_CLASSES)) {
    for (const node of Array.from(container.querySelectorAll(tag))) {
      // Preserve what the renderer already set (images size themselves).
      node.className = node.className === '' ? classes : `${node.className} ${classes}`;
    }
  }
}

/** The longest slug, in code points (not UTF-16 units — a CJK slug must not split a pair). */
const SLUG_MAX = 60;
/** The diacritics the original slugger folded away (`é` → `e`, `ā` → `a`). */
const LATIN_DIACRITIC = /^[\u0300-\u036f]$/;
const ASCII_SLUG_CHAR = /^[a-z0-9]$/;
/** A letter, digit or mark of any script… */
const WORD_CHAR = /^[\p{L}\p{N}\p{M}]$/u;
/** …except the ones the original slugger turned into a hyphen, which it still does. */
const LEGACY_SEPARATOR = /^[\p{ASCII}\p{Script=Latin}]$/u;

/** A letter or digit the original `[a-z0-9]` slugger would have thrown away. */
function isForeignWordChar(ch: string): boolean {
  return WORD_CHAR.test(ch) && !LEGACY_SEPARATOR.test(ch);
}

/**
 * `A long heading!` → `a-long-heading`; `返金ポリシー` → `返金ポリシー`;
 * `Политика возврата` → `политика-возврата`. Empty input yields `section`.
 *
 * ── WHY IT IS BUILT THIS WAY ────────────────────────────────────────────────
 * The first version kept only `[a-z0-9]`, so in Japanese and Russian every
 * heading collapsed to `section`, `section-2`, … and a deep link into
 * `/legal/terms#…` pointed at whatever section happened to be Nth. Now letters,
 * digits and marks of every script survive.
 *
 * LATIN SLUGS ARE BYTE-IDENTICAL TO THE ORIGINAL, on purpose: English (and
 * German, Latvian, Turkish…) links have already been shared. So for Latin text
 * the old rules still hold exactly — diacritics are folded (`ā` → `a`), and a
 * Latin letter that does not fold into `a-z` (`ß`, `ı`, `ø`) is still a
 * separator. `test/heading-slugs.test.ts` restates the old function and checks
 * the two agree over every real legal heading and a corpus per Latin locale.
 *
 * The diacritic fold is applied ONLY after a Latin (or ASCII) base. After a
 * Cyrillic or Greek letter the same combining marks are part of the letter —
 * NFKD splits `й` into `и` + U+0306 — so they are kept and recomposed by NFKC,
 * which also folds full-width / half-width forms (`３` → `3`, `ｶﾞ` → `ガ`).
 */
export function slugifyHeading(text: string): string {
  let folded = '';
  let foreignBase = false;
  for (const ch of text.toLowerCase().normalize('NFKD')) {
    if (LATIN_DIACRITIC.test(ch)) {
      if (foreignBase) folded += ch;
      continue;
    }
    if (!/^\p{M}$/u.test(ch)) foreignBase = isForeignWordChar(ch);
    folded += ch;
  }

  let slug = '';
  let gap = false;
  let lastKeptForeign = false;
  for (const ch of folded.normalize('NFKC')) {
    const isMark = /^\p{M}$/u.test(ch);
    const keep = isMark ? lastKeptForeign && !gap : ASCII_SLUG_CHAR.test(ch) || isForeignWordChar(ch);
    if (!keep) {
      gap = true;
      continue;
    }
    if (gap && slug !== '') slug += '-';
    gap = false;
    slug += ch;
    if (!isMark) lastKeptForeign = !ASCII_SLUG_CHAR.test(ch);
  }

  const capped = Array.from(slug).slice(0, SLUG_MAX).join('');
  return capped === '' ? 'section' : capped;
}

/**
 * Give every `<h2>` a unique id and return the pairs, for a table of contents.
 *
 * Ids are deduplicated because two sections may share a title, and a duplicate
 * id makes the second anchor unreachable — the first one wins forever.
 */
export function indexHeadings(container: HTMLElement): { id: string; text: string }[] {
  const used = new Set<string>();
  const out: { id: string; text: string }[] = [];
  for (const heading of Array.from(container.querySelectorAll('h2'))) {
    const text = (heading.textContent ?? '').trim();
    if (text === '') continue;
    const base = slugifyHeading(text);
    let id = base;
    let n = 2;
    while (used.has(id)) id = `${base}-${n++}`;
    used.add(id);
    heading.id = id;
    out.push({ id, text });
  }
  return out;
}
